/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";
import StyledBreadcrumbs from "../../components/general/StyledBreadcrumbs";
import { Effect, Nullable, Path, StorageLocation, TechwiseOfficeDetailed } from "../../utils/Interfaces";
import {
    Box,
    Button,
    Typography,
    Stack,
    Card,
    CardContent,
    CircularProgress,
    Tooltip,
    IconButton,
    TextField,
    Alert,
    Link,
} from "@mui/material";

import { NavigateFunction, useNavigate, useParams } from "react-router-dom";
import {
    deleteStorageLocation,
    deleteTechwiseOffice,
    modifyTechwiseOffice,
    techwiseOfficeDetails,
} from "./techwiseOfficeFunctions";

import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { Check, Clear, Delete, Edit } from "@mui/icons-material";
import { StateMapping, STATES_MAPPING } from "../../utils/Constants";
import { getStateMapping } from "../../utils/Functions";
import ControlledEditableField from "../../components/form-controls/ControlledEditableField";
import Autocomplete from "@mui/material/Autocomplete";
import DataTable from "../../components/table/DataTable";
import { useWindowDimensions } from "../../utils/Hooks";
import { GridRenderCellParams } from "@mui/x-data-grid";
import AddLocationModal from "./AddLocationModal";
import ConfirmDeleteModal from "../../components/confirmation/ConfirmDeleteModal";
import { updateTechwiseOffices } from "../materials-techwise-offices/techwiseOfficesFunctions";
import ModifyLocationModal from "./ModifyLocationModal";
import { useAppDispatch } from "../../app/hooks";

const TechwiseOfficeDetails = () => {
    const { officeId } = useParams();
    const dispatch: Dispatch<AnyAction> = useAppDispatch();
    const [office, setOffice] = useState<Nullable<TechwiseOfficeDetailed>>(null);

    const navigate: NavigateFunction = useNavigate();

    const { height } = useWindowDimensions();

    const [selectedStorageLocation, setSelectedStorageLocation] = useState<Nullable<StorageLocation>>(null);

    const [editingHeader, setEditingHeader] = useState<boolean>(false);
    const [confirmDeleteTechwiseOfficeOpen, setConfirmDeleteTechwiseOfficeOpen] = useState<boolean>(false);
    const [addLocationModalOpen, setAddLocationModalOpen] = useState<boolean>(false);
    const [modifyStorageLocationModalOpen, setModifyStorageLocationModalOpen] = useState<boolean>(false);
    const [confirmDeleteStorageLocationOpen, setConfirmDeleteStorageLocationOpen] = useState<boolean>(false);

    // These fields are used to handle built-in editing without a modal dialog.
    const [officeName, setOfficeName] = useState<string>("");
    const [officeStreetAddress, setOfficeStreetAddress] = useState<string>("");
    const [officeCity, setOfficeCity] = useState<string>("");
    const [officeState, setOfficeState] = useState<StateMapping>(STATES_MAPPING[46]);
    const [officeZipCode, setOfficeZipCode] = useState<string>("");

    function updateOfficeState(office: TechwiseOfficeDetailed) {
        setOffice(office);
        setOfficeName(office.name);
        setOfficeStreetAddress(office.street_address);
        setOfficeCity(office.city);
        setOfficeState(getStateMapping(office.state));
        setOfficeZipCode(office.zip_code.toString());
    }

    function getHeaderFormErrors(): string {
        if (officeName.trim() === "") {
            return "Name cannot be blank.";
        }

        if (officeStreetAddress.trim() === "") {
            return "Street Address cannot be blank.";
        }

        if (officeCity.trim() === "") {
            return "City cannot be blank.";
        }

        if (officeZipCode.trim() === "") {
            return "Zip Code cannot be blank.";
        }

        let zipCodeNumber = parseInt(officeZipCode);

        if (zipCodeNumber < 10000 || zipCodeNumber >= 100000) {
            return "Zip Code cannot be lower than 10,000 or greater than 100,000.";
        }

        return "";
    }

    useEffect(() => {
        techwiseOfficeDetails(parseInt(officeId ?? "1"), dispatch, updateOfficeState);
    }, [officeId]);

    const editCol = {
        field: "edit",
        headerName: "Edit",
        filterable: false,
        sortable: false,
        width: 40,
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Tooltip title={`Edit ${params.row.name}`}>
                    <IconButton
                        onClick={() => {
                            setSelectedStorageLocation(params.row);
                            setModifyStorageLocationModalOpen(true);
                        }}
                    >
                        <Edit />
                    </IconButton>
                </Tooltip>
            );
        },
    };

    const deleteCol = {
        field: "delete",
        headerName: "Delete",
        filterable: false,
        sortable: false,
        width: 80,
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Tooltip title={`Delete ${params.row.name}`}>
                    <IconButton
                        onClick={() => {
                            setSelectedStorageLocation(params.row);
                            setConfirmDeleteStorageLocationOpen(true);
                        }}
                    >
                        <Delete color="error" />
                    </IconButton>
                </Tooltip>
            );
        },
    };

    const locationNameCol = {
        field: "name",
        headerName: "Name",
        filterable: true,
        sortable: true,
        width: 250,
        valueGetter: (params: GridRenderCellParams) => {
            return params.row.name;
        },
        renderCell: (params: GridRenderCellParams) => {
            return (
                <Link onClick={() => navigate(`/materials/locations/${params.row.id}`)} sx={{ cursor: "pointer" }}>
                    <Typography>{params.row.name}</Typography>
                </Link>
            );
        },
    };

    const locationTypeCol = {
        field: "location_type",
        headerName: "Location Type",
        filterable: true,
        sortable: true,
        width: 250,
    };

    const responsibleUserCol = {
        field: "responsible_user",
        headerName: "Responsible User",
        filterable: true,
        sortable: true,
        width: 250,
        valueGetter: (params: GridRenderCellParams) => {
            if (!params.row.responsible_user) {
                return "--";
            }
            return `${params.row.responsible_user.first_name} ${params.row.responsible_user.last_name}`;
        },
    };

    let locationColumns: any[] = [editCol, deleteCol, locationNameCol, locationTypeCol, responsibleUserCol];

    let breadcrumbPaths: Nullable<Path>[] = [
        { text: "Techwise Portal", route: "/" },
        { text: "Parts/Materials", route: "/?section=materials" },
        { text: "Techwise Offices", route: "/materials/techwise-offices" },
    ];

    if (office === null) {
        breadcrumbPaths.push(null);
    } else {
        breadcrumbPaths.push({ text: office.name, route: `/materials/techwise-office/${office.id}` });
    }

    if (office === null) {
        return (
            <Box>
                <StyledBreadcrumbs paths={breadcrumbPaths} />
                <Stack direction="row" justifyContent="center" sx={{ mt: 40 }}>
                    <CircularProgress size={60} />
                </Stack>
            </Box>
        );
    }

    return (
        <Box>
            <StyledBreadcrumbs paths={breadcrumbPaths} />

            <AddLocationModal
                open={addLocationModalOpen}
                handleClose={() => setAddLocationModalOpen(false)}
                techwiseOffice={office}
                onStorageLocationCreated={() => techwiseOfficeDetails(parseInt(officeId ?? "1"), dispatch, updateOfficeState)}
            />

            <ModifyLocationModal
                open={modifyStorageLocationModalOpen}
                handleClose={() => setModifyStorageLocationModalOpen(false)}
                techwiseOffice={office}
                storageLocation={selectedStorageLocation}
                onStorageLocationModified={() => techwiseOfficeDetails(parseInt(officeId ?? "1"), dispatch, updateOfficeState)}
            />

            <ConfirmDeleteModal
                confirmText={`delete ${office.name}`}
                description={`You are about to delete the Techwise Office ${office.name}. This cannot be undone and will also delete all contained storage locations and inventories.`}
                onDelete={(modalCallback: Effect) => {
                    deleteTechwiseOffice(office.id, dispatch, () => {
                        modalCallback();
                        updateTechwiseOffices(dispatch, () => {});
                        navigate("/materials/techwise-offices");
                    });
                }}
                open={confirmDeleteTechwiseOfficeOpen}
                handleClose={() => setConfirmDeleteTechwiseOfficeOpen(false)}
            />

            <ConfirmDeleteModal
                confirmText={`delete ${selectedStorageLocation?.name}`}
                description={`You are about to delete the Techwise Office ${selectedStorageLocation?.name}. This cannot be undone and will also delete all contained storage locations and inventories.`}
                onDelete={(modalCallback: Effect) => {
                    deleteStorageLocation(selectedStorageLocation?.id ?? 0, dispatch, () => {
                        techwiseOfficeDetails(parseInt(officeId ?? "1"), dispatch, (techwiseOffice: TechwiseOfficeDetailed) => {
                            updateOfficeState(techwiseOffice);
                            modalCallback();
                        });
                    });
                }}
                open={confirmDeleteStorageLocationOpen}
                handleClose={() => setConfirmDeleteStorageLocationOpen(false)}
            />

            <Card sx={{ mt: 3 }}>
                <CardContent>
                    {editingHeader && getHeaderFormErrors() === "" && (
                        <Alert severity="success" sx={{ mb: 3 }}>
                            All changes are valid.
                        </Alert>
                    )}

                    {editingHeader && getHeaderFormErrors() !== "" && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {getHeaderFormErrors()}
                        </Alert>
                    )}

                    <Stack direction="row" justifyContent="space-between">
                        <ControlledEditableField<string>
                            value={officeName}
                            setValue={setOfficeName}
                            width={600}
                            label="Name"
                            editing={editingHeader}
                            size="small"
                            typograhyVariant="h5"
                        />

                        {editingHeader && (
                            <Box>
                                <Tooltip title="Cancel Changes">
                                    <IconButton
                                        onClick={() => {
                                            setEditingHeader(false);
                                            updateOfficeState(office);
                                        }}
                                    >
                                        <Clear />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Submit Changes">
                                    <IconButton
                                        disabled={getHeaderFormErrors() !== ""}
                                        onClick={() => {
                                            modifyTechwiseOffice(
                                                office.id,
                                                {
                                                    name: officeName,
                                                    street_address: officeStreetAddress,
                                                    city: officeCity,
                                                    state: officeState.abbrev,
                                                    zip_code: parseInt(officeZipCode),
                                                },
                                                dispatch,
                                                () => {
                                                    techwiseOfficeDetails(
                                                        parseInt(officeId ?? "1"),
                                                        dispatch,
                                                        (office: TechwiseOfficeDetailed) => {
                                                            updateOfficeState(office);
                                                            setEditingHeader(false);
                                                        }
                                                    );
                                                }
                                            );
                                        }}
                                    >
                                        <Check />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}

                        {!editingHeader && (
                            <Box>
                                <Tooltip title={`Edit ${office?.name}`}>
                                    <IconButton onClick={() => setEditingHeader(true)}>
                                        <Edit />
                                    </IconButton>
                                </Tooltip>
                                {office?.deletable && (
                                    <Tooltip title={`Delete ${office?.name}`}>
                                        <IconButton onClick={() => setConfirmDeleteTechwiseOfficeOpen(true)}>
                                            <Delete color="error" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        )}
                    </Stack>

                    <Box sx={{ mb: 3 }} />

                    <ControlledEditableField<string>
                        value={officeStreetAddress}
                        setValue={setOfficeStreetAddress}
                        width={600}
                        label="Street Address"
                        editing={editingHeader}
                        size="small"
                    />

                    <Stack direction="row" justifyContent="flex-start" sx={{ mt: editingHeader ? 3 : 1 }}>
                        <ControlledEditableField<string>
                            value={officeCity}
                            setValue={setOfficeCity}
                            width={400}
                            label="City"
                            editing={editingHeader}
                            size="small"
                        />

                        {!editingHeader && <Typography>,&nbsp;{officeState.abbrev}&nbsp;</Typography>}

                        {editingHeader && (
                            <Autocomplete
                                size="small"
                                disablePortal
                                onChange={(_event, value) => {
                                    setOfficeState(value ? value : STATES_MAPPING[46]);
                                }}
                                id="state"
                                options={STATES_MAPPING}
                                value={officeState}
                                getOptionKey={(option: StateMapping) => option.abbrev}
                                getOptionLabel={(option: StateMapping) => option.name}
                                renderInput={(params) => (
                                    <TextField sx={{ ml: 3, mr: 3, width: 200 }} required {...params} label="State" />
                                )}
                            />
                        )}

                        <ControlledEditableField<string>
                            value={officeZipCode}
                            setValue={setOfficeZipCode}
                            width={200}
                            label="Zip Code"
                            editing={editingHeader}
                            size="small"
                            textFieldVariant="number"
                        />
                    </Stack>
                </CardContent>
            </Card>

            <Box sx={{ mb: 3 }} />

            <Card>
                <CardContent>
                    <Typography variant="h5">Storage Locations</Typography>
                    <Box sx={{ mb: 3 }} />
                    <DataTable
                        tableName="TECHWISE_OFFICE_DETAILS"
                        height={height - 450}
                        columns={locationColumns}
                        data={office.locations}
                    >
                        <Button onClick={() => setAddLocationModalOpen(true)}>Add Storage Location</Button>
                    </DataTable>
                </CardContent>
            </Card>
        </Box>
    );
};

export default TechwiseOfficeDetails;
